<!-- PermanentDeleteModal.vue -->
<script setup lang="ts">
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import GlobalFormMessage from '@/components/forms/GlobalFormMessage.vue'
import { useGlobalFormMessages } from '@/composables/useGlobalFormMessages'
import type { User } from '@/types/user'
import type { UserType } from '@/utils/roleHandler'
import { ref, computed, watch } from 'vue'
import OctagonExclamationIcon from '@/icons/OctagonExclamationIcon.vue'

const props = defineProps<{
  user: User | null
  loading: boolean
  error?: string | null
}>()

// Use the form errors composable
const {
  showErrors,
  addError,
  clearErrors,
  removeError,
  mainError
} = useGlobalFormMessages()

const deleteConfirmation = ref('')

// Watch for external error changes
watch(() => props.error, (newError) => {
  if (newError) {
    addError({
      message: newError,
      type: 'error'
    })
  }
})

// Helper function to get name - handles both camelCase and snake_case
const getUserName = (field: 'firstName' | 'lastName'): string => {
  if (!props.user) return ''
  
  if (field === 'firstName') {
    // Try camelCase first, then snake_case, then fallback
    return props.user.firstName || 
           (props.user as { first_name?: string }).first_name || 
           ''
  } else {
    return props.user.lastName || 
           (props.user as { last_name?: string }).last_name || 
           ''
  }
}

// Helper function to get display name
const getDisplayName = (): string => {
  if (!props.user) return 'No name'
  
  const firstName = getUserName('firstName')
  const lastName = getUserName('lastName')
  const fullName = `${firstName} ${lastName}`.trim()
  return fullName || props.user.username || props.user.email || 'No name'
}

// Helper function to get initials
const getInitials = (): string => {
  if (!props.user) return 'U'
  
  const firstName = getUserName('firstName')
  const lastName = getUserName('lastName')
  return `${firstName.charAt(0) || ''}${lastName.charAt(0) || ''}`.toUpperCase() || 'U'
}

// Computed property for validation error
const confirmationError = computed(() => {
  if (!showErrors.value) return ''
  
  if (!deleteConfirmation.value) {
    return 'Please type "DELETE" to confirm'
  }
  
  if (deleteConfirmation.value.toUpperCase() !== 'DELETE') {
    return 'You must type exactly "DELETE" (case-insensitive)'
  }
  
  return ''
})

// Clear input and errors when input changes
const handleInputChange = () => {
  clearErrors()
}

// Get user type label
const getUserTypeLabel = (userType: UserType) => {
  const labelMap: Record<string, string> = {
    admin: 'Administrator',
    agent: 'Agent',
    engineer: 'Engineer',
    client: 'Client',
  }
  return labelMap[userType] || userType
}

// Format date
const formatDate = (dateString: string): string => {
  if (!dateString) return 'Never'
  try {
    const date = new Date(dateString)
    if (isNaN(date.getTime())) return dateString
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return dateString
  }
}
</script>

<template>
  <div class="space-y-4">
    <div class="bg-red-50 border-l-4 border-red-400 p-4">
      <div class="flex">
        <div class="flex-shrink-0">
          <OctagonExclamationIcon class="w-8 h-8 text-red-400" />
        </div>
        <div class="ml-3">
          <p class="text-sm font-medium text-red-700 mb-1">
            PERMANENT DELETION WARNING
          </p>
          <p class="text-sm text-red-700">
            This action will <strong>permanently delete</strong> the user from the database.
            This action is <strong>irreversible</strong> and all user data will be lost forever.
          </p>
        </div>
      </div>
    </div>

    <div v-if="props.user" class="bg-gray-50 p-4 rounded-lg">
      <h3 class="text-sm font-medium text-gray-700 mb-2">User Details:</h3>
      <div class="space-y-3">
        <div class="flex items-center gap-3">
          <div
            class="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-600"
          >
            {{ getInitials() }}
          </div>
          <div>
            <p class="font-medium">
              {{ getDisplayName() }}
            </p>
            <p class="text-sm text-gray-500">{{ props.user.email }}</p>
            <p v-if="props.user.username" class="text-sm text-gray-400">
              @{{ props.user.username }}
            </p>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span class="text-gray-600">User Type:</span>
            <span class="font-medium ml-2">{{ getUserTypeLabel(props.user.userType) }}</span>
          </div>
          <div>
            <span class="text-gray-600">Status:</span>
            <span class="font-medium ml-2">{{ props.user.status }}</span>
          </div>
          <div>
            <span class="text-gray-600">Created:</span>
            <span class="font-medium ml-2">{{ formatDate(props.user.createdAt) }}</span>
          </div>
          <div>
            <span class="text-gray-600">Last Login:</span>
            <span class="font-medium ml-2">{{ formatDate(props.user.lastLogin || '') }}</span>
          </div>
        </div>

        <div v-if="props.user.notes" class="pt-2 border-t border-gray-200">
          <span class="text-gray-600">Notes:</span>
          <p class="text-sm text-gray-700 mt-1">{{ props.user.notes }}</p>
        </div>
      </div>
    </div>

    <!-- Confirmation input section -->
    <div class="bg-yellow-50 border border-yellow-200 rounded p-4">
      <label class="block text-sm font-medium text-yellow-800 mb-2">
        Type "DELETE" to confirm permanent deletion:
      </label>
      
      <TextInputDefault
        v-model="deleteConfirmation"
        labelText=""
        placeholder="Type DELETE here"
        id="confirmDeleteInput"
        name="confirmDeleteInput"
        type="text"
        @update:modelValue="handleInputChange"
        :errorMessage="confirmationError"
        :errorType="confirmationError ? 'error' : 'info'"
      />
      
      <p class="text-xs text-yellow-600 mt-1">
        This is a safety measure to prevent accidental permanent deletion.
      </p>
    </div>

    <!-- Global Form Messages -->
    <div class="space-y-2">
      <GlobalFormMessage
        v-if="mainError"
        :message="mainError.message"
        :type="mainError.type"
        @dismiss="removeError(mainError!.message)"
      />
      
      <!-- Prop error from parent -->
      <GlobalFormMessage
        v-if="props.error && !mainError"
        :message="props.error"
        type="error"
      />
    </div>
  </div>
</template>